import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch schools for the Universities section
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/schools/public`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch schools');
        }
        
        const data = await response.json();
        setSchools(data);
      } catch (error) {
        console.error('Error fetching schools:', error);
        // Still show default UI for UCF
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchools();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-black py-2 shadow-lg' : 'bg-transparent py-4'}`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">
              <span className="text-red-600">EDX</span> Red
            </h1>
          </div>
          <div className="hidden md:flex space-x-8">
            <a href="#features" className="hover:text-red-500 transition-colors">Features</a>
            <a href="#universities" className="hover:text-red-500 transition-colors">Universities</a>
            <a href="#faq" className="hover:text-red-500 transition-colors">FAQ</a>
          </div>
          <div className="flex space-x-4">
            <Link 
              to="/login" 
              className="px-4 py-2 border border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors"
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors hidden md:block"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
          <div className="absolute inset-0 bg-[url('https://via.placeholder.com/1920x1080')] bg-cover bg-center mix-blend-overlay"></div>
        </div>
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-3xl">
            <h1 className="text-6xl md:text-7xl font-extrabold mb-6">
              Your University. <br />
              <span className="text-red-600">Your Resources.</span> <br />
              One Platform.
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              EDX Red connects university students with the resources they need to succeed.
              Share notes, past exams, and collaborate with peers from your institution.
              Ask questions to peers who understand your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/register" 
                className="px-8 py-4 bg-red-600 text-white rounded-md font-medium transition-colors hover:bg-red-700 text-center"
              >
                Get Started for Free
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden lg:block">
          <a href="#features">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">
            Unlock Your Academic <span className="text-red-600">Potential</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg transform transition-transform hover:scale-105">
              <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4">Course Materials</h3>
              <p className="text-gray-300">
                Access lecture notes, practice exams, and study guides specific to your courses. 
                Materials are uploaded by students and organized by course sections.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg transform transition-transform hover:scale-105">
              <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4">Peer Collaboration</h3>
              <p className="text-gray-300">
                Connect with students from your specific courses. Join course sections,
                discuss assignments, and help each other succeed through direct collaboration.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg transform transition-transform hover:scale-105">
              <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4">Smart Search</h3>
              <p className="text-gray-300">
                Find exactly what you need with our advanced search. Filter materials by course,
                professor, term, and content type to quickly access relevant resources.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg transform transition-transform hover:scale-105">
              <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-4">Questions</h3>
              <p className="text-gray-300">
                Ask questions specific to your courses, and get answers from peers who understand your situation and materials.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Universities Section */}
      <section id="universities" className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Supported <span className="text-red-600">Universities</span>
          </h2>
          <p className="text-xl text-center text-gray-400 mb-16 max-w-3xl mx-auto">
            We're expanding our network of supported universities. Here's where we're currently operating
            and where we're heading next.
          </p>
          
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Display actual schools */}
              {schools && schools.length > 0 ? (
                schools.map(school => (
                  <div key={school.id} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg border border-gray-800 transform transition-transform hover:scale-105">
                    <div className="h-48 bg-gray-800 flex items-center justify-center">
                      {school.logoUrl ? (
                        <img src={school.logoUrl} alt={school.name} className="max-h-32 max-w-full" />
                      ) : (
                        <div className="text-6xl font-bold" style={{ color: school.primaryColor || '#ff0000' }}>
                          {school.name}
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      {school.isSignupPaused ? (
                        <div className="flex items-center mb-4">
                          <span className="bg-yellow-500 rounded-full w-3 h-3 mr-2"></span>
                          <span className="text-yellow-500 font-semibold">Coming Soon</span>
                        </div>
                      ) : (
                        <div className="flex items-center mb-4">
                          <span className="bg-green-500 rounded-full w-3 h-3 mr-2"></span>
                          <span className="text-green-500 font-semibold">Available Now</span>
                        </div>
                      )}
                      <h3 className="text-xl font-bold mb-2">{school.name}</h3>
                      <p className="text-gray-400 mb-4">
                        {school.isSignupPaused 
                          ? `We're currently working on adding ${school.name} to our platform. Join the waitlist to be notified when we launch.`
                          : `EDX Red is fully available at ${school.name}. Sign up with your ${school.domain} email to get started.`
                        }
                      </p>
                      {school.isSignupPaused ? (
                        <Link 
                          to={`/waitlist?email=@${school.domain}`}
                          className="block text-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors"
                        >
                          Join Waitlist
                        </Link>
                      ) : (
                        <Link 
                          to="/register" 
                          className="block text-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                        >
                          Sign Up Now
                        </Link>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                // Default UCF card if no schools fetched
                <div className="bg-gray-900 rounded-lg overflow-hidden shadow-lg border border-gray-800 transform transition-transform hover:scale-105">
                  <div className="h-48 bg-gray-800 flex items-center justify-center">
                    <div className="text-6xl font-bold text-red-600">UCF</div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="bg-yellow-500 rounded-full w-3 h-3 mr-2"></span>
                      <span className="text-yellow-500 font-semibold">Coming Soon</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">University of Central Florida</h3>
                    <p className="text-gray-400 mb-4">
                      We're currently working on adding UCF to our platform. Stay tuned for updates!
                    </p>
                    <Link 
                      to="/waitlist?email=@ucf.edu" 
                      className="block text-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors"
                    >
                      Join Waitlist
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">
            Frequently Asked <span className="text-red-600">Questions</span>
          </h2>
          
          <div className="max-w-3xl mx-auto">
          <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">How long will the platform be in Alpha?</h3>
              <p className="text-gray-300">
                EDX Red will remain in the Alpha phase while preliminary testing is still ongoing. 
                Features and pricing are not final and may change on the full release.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">Is EDX Red free to use?</h3>
              <p className="text-gray-300">
                During the Alpha phase, we have a Basic and Pro plan. The Basic plan is completely free, while the Pro plan costs $1.99/month and includes AI functionality. We believe in keeping educational resources accessible to all students.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">How do you verify that users are actually students?</h3>
              <p className="text-gray-300">
                We verify student status through university email domains. You must register with your official university
                email address and complete the verification process.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">When will my university be added to EDX Red?</h3>
              <p className="text-gray-300">
                We're expanding our university network rapidly. If your school isn't currently supported, you can join our waitlist,
                and we'll notify you as soon as we're ready to launch at your institution. We prioritize schools based on 
                student demand.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">What is the waitlist and how does it work?</h3>
              <p className="text-gray-300">
                Our waitlist allows students from universities we're planning to support to express interest. When you join the waitlist,
                we'll notify you by email as soon as we're ready to launch at your school. This helps us prioritize which schools to add next.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">How can I contribute to EDX Red?</h3>
              <p className="text-gray-300">
                You can contribute by uploading your study materials, participating in discussions, and providing feedback
                on other resources. Quality contributions increase your reputation within the platform.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-black py-12 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">
                <span className="text-red-600">EDX</span> Red
              </h3>
              <p className="text-gray-400">
                The Educational Exchange Platform for University Students
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-red-500 transition-colors">Features</a></li>
                <li><a href="#universities" className="text-gray-400 hover:text-red-500 transition-colors">Universities</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-red-500 transition-colors">FAQ</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Academic Integrity</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Copyright Policy</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Support</a></li>
                <li><a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Report an Issue</a></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} EDX Red. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;